// @flow
import React, { Component } from 'react';
import PageTemplate from 'components/base/PageTemplate';
import MainMobileHeadContainer from 'containers/main/MainMobileHeadContainer';
import MainHeadContainer from 'containers/main/MainHeadContainer';
import { type ContextRouter } from 'react-router-dom';
import './AI.scss';

const MEILISEARCH_URL = 'https://ms-ai.cacheby.com';
const MEILISEARCH_KEY = '8b302632c257a5524b97753d6abe0d731a802fc55500080145e1f7f44551f197';

type SearchParams = {
  keywords: string,
  maxPrice: ?number,
  minPrice: ?number,
};

type Message = {
  role: 'user' | 'assistant',
  content: string,
  products?: Array<Object>,
  searchParams?: SearchParams,
  hasMoreProducts?: boolean,
  originalProduct?: Object,
};

type AttachedFile = {
  file: File,
  preview: string,
  type: 'image' | 'pdf' | 'excel',
};

type State = {
  messages: Array<Message>,
  inputValue: string,
  isProcessing: boolean,
  conversationHistory: Array<{ role: string, content: string }>,
  attachedFiles: Array<AttachedFile>,
};

type Props = ContextRouter;

const getContentFromParsed = (parsed: Object): string => {
  const { choices } = parsed;
  if (!choices || !choices[0] || !choices[0].delta) return '';
  const { content } = choices[0].delta;
  return content || '';
};

const parseSSELines = (text: string): string => {
  const lines = text.split('\n');
  let result = '';
  lines.forEach(line => {
    if (line.startsWith('data: ') && line !== 'data: [DONE]') {
      try {
        const parsed = JSON.parse(line.slice(6));
        result += getContentFromParsed(parsed);
      } catch (e) {
        // ignore parse errors
      }
    }
  });
  return result;
};

class AI extends Component<Props, State> {
  chatMessagesRef: { current: null | HTMLDivElement };
  inputRef: { current: null | HTMLTextAreaElement };
  fileInputRef: { current: null | HTMLInputElement };

  constructor(props: Props) {
    super(props);
    this.state = {
      messages: [],
      inputValue: '',
      isProcessing: false,
      conversationHistory: [],
      attachedFiles: [],
    };
    this.chatMessagesRef = React.createRef();
    this.inputRef = React.createRef();
    this.fileInputRef = React.createRef();
  }

  scrollToBottom = () => {
    if (this.chatMessagesRef.current) {
      this.chatMessagesRef.current.scrollTop = this.chatMessagesRef.current.scrollHeight;
    }
  };

  handleInputChange = (e: SyntheticInputEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    this.setState({ inputValue: textarea.value });
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 340)}px`;
  };

  handleKeyDown = (e: SyntheticKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      this.sendMessage();
    }
  };

  sendExample = (text: string) => {
    if (text === '파트넘버 대체품 찾기') {
      this.setState({ inputValue: '' });
      if (this.inputRef.current) {
        this.inputRef.current.placeholder = '파트넘버를 입력하세요 (예: HA-HSC565-14-EA 대체품 찾아줘)';
        this.inputRef.current.focus();
      }
      return;
    }
    this.setState({ inputValue: text }, () => {
      this.sendMessage();
    });
  };

  findAlternatives = (product: Object) => {
    const query = `"${product.title}" 제품의 대체품을 찾아주세요. 비슷한 기능을 하는 다른 브랜드나 모델을 추천해주세요.`;
    this.setState({ inputValue: query }, () => {
      this.sendMessage();
    });
  };

  handleFileSelect = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newFiles: Array<AttachedFile> = [];

    files.forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      file.type === 'application/vnd.ms-excel' ||
                      file.name.endsWith('.xlsx') ||
                      file.name.endsWith('.xls');

      if (isImage || isPDF || isExcel) {
        const preview = isImage ? URL.createObjectURL(file) : '';
        let fileType: 'image' | 'pdf' | 'excel' = 'image';
        if (isPDF) fileType = 'pdf';
        else if (isExcel) fileType = 'excel';

        newFiles.push({
          file,
          preview,
          type: fileType,
        });
      }
    });

    this.setState(prevState => ({
      attachedFiles: [...prevState.attachedFiles, ...newFiles],
    }));

    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  removeFile = (index: number) => {
    this.setState(prevState => {
      const files = [...prevState.attachedFiles];
      if (files[index] && files[index].preview) {
        URL.revokeObjectURL(files[index].preview);
      }
      files.splice(index, 1);
      return { attachedFiles: files };
    });
  };

  fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const { result } = reader;
        if (typeof result === 'string') {
          resolve(result.split(',')[1]);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  extractTextFromPDF = async (file: File): Promise<string> => {
    const base64 = await this.fileToBase64(file);
    return `[PDF 파일: ${file.name}]\n(PDF 내용 분석을 위해 첨부됨)`;
  };

  searchByPartNumber = async (partNumber: string): Promise<Object | null> => {
    try {
      const response = await fetch(`${MEILISEARCH_URL}/indexes/products/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${MEILISEARCH_KEY}`,
        },
        body: JSON.stringify({
          q: partNumber,
          limit: 1,
          attributesToRetrieve: ['id', 'title', 'brand', 'min_price', 'max_price', 'url_slug', 'thumbnail', 'username', 'category', 'description'],
        }),
      });

      const data = await response.json();
      if (data.hits && data.hits.length > 0) {
        return data.hits[0];
      }
    } catch (error) {
      console.error('Part number search error:', error);
    }
    return null;
  };

  detectPartNumber = (text: string): string | null => {
    const patterns = [
      /([A-Z]{1,4}[-]?[A-Z0-9]{2,}[-]?[A-Z0-9]*)/gi,
      /(\d{2,}[-]?[A-Z]{1,4}[-]?[A-Z0-9]*)/gi,
    ];

    const matches = [];
    patterns.forEach(pattern => {
      const found = text.match(pattern);
      if (found) {
        matches.push(...found);
      }
    });

    if (matches.length > 0) {
      return matches.sort((a, b) => b.length - a.length)[0];
    }
    return null;
  };

  extractSearchParams = async (): Promise<{ keywords: string, maxPrice: ?number, minPrice: ?number }> => {
    const { conversationHistory } = this.state;
    try {
      const contextSummary = conversationHistory.map(m => `${m.role}: ${m.content}`).join('\n');

      const response = await fetch(`${MEILISEARCH_URL}/chats/research-recommender/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${MEILISEARCH_KEY}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `대화 내용을 분석하여 제품 검색 조건을 JSON으로 추출하세요.

파트넘버 대체품 요청인 경우:
- 파트넘버 자체와 제품 종류를 키워드에 포함
- 예: "HA-HSC565 centrifuge" 또는 "원심분리기"

반드시 아래 JSON 형식으로만 응답하세요 (다른 텍스트 없이):
{
  "keywords": "검색할 제품 키워드 (파트넘버 + 제품종류)",
  "maxPrice": 최대가격숫자또는null,
  "minPrice": 최소가격숫자또는null
}`,
            },
            {
              role: 'user',
              content: `대화 내용:\n${contextSummary}\n\n위 대화에서 검색할 제품과 가격 조건을 추출하세요.`,
            },
          ],
          stream: true,
        }),
      });

      const text = await response.text();
      const jsonStr = parseSSELines(text);

      const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Search params extraction error:', error);
    }

    const lastUserMsg = conversationHistory.filter(m => m.role === 'user').pop();
    return { keywords: (lastUserMsg && lastUserMsg.content) || '', maxPrice: null, minPrice: null };
  };

  searchProducts = async (
    params: { keywords: string, maxPrice: ?number, minPrice: ?number },
    offset: number = 0,
  ): Promise<{ hits: Array<Object>, hasMore: boolean }> => {
    const { keywords, maxPrice, minPrice } = params;
    const filter = [];
    if (maxPrice) filter.push(`min_price <= ${maxPrice}`);
    if (minPrice) filter.push(`min_price >= ${minPrice}`);

    const limit = 6;
    const searchBody: Object = {
      q: keywords,
      limit,
      offset,
      attributesToRetrieve: ['id', 'title', 'brand', 'min_price', 'max_price', 'url_slug', 'thumbnail', 'username'],
    };

    if (filter.length > 0) {
      searchBody.filter = filter.join(' AND ');
    }

    try {
      const response = await fetch(`${MEILISEARCH_URL}/indexes/products/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${MEILISEARCH_KEY}`,
        },
        body: JSON.stringify(searchBody),
      });

      const data = await response.json();
      const hits = data.hits || [];
      const total = data.estimatedTotalHits || 0;
      return { hits, hasMore: offset + hits.length < total };
    } catch (error) {
      console.error('Product search error:', error);
      return { hits: [], hasMore: false };
    }
  };

  loadMoreProducts = async (messageIndex: number) => {
    const { messages } = this.state;
    const message = messages[messageIndex];
    if (!message || !message.searchParams || !message.products) return;

    const offset = message.products.length;
    const { hits, hasMore } = await this.searchProducts(message.searchParams, offset);

    if (hits.length > 0) {
      this.setState(prevState => {
        const newMessages = [...prevState.messages];
        newMessages[messageIndex] = {
          ...newMessages[messageIndex],
          products: [...(newMessages[messageIndex].products || []), ...hits],
          hasMoreProducts: hasMore,
        };
        return { messages: newMessages };
      });
      this.scrollToBottom();
    }
  };

  processStreamChunk = (
    reader: Object,
    decoder: Object,
    fullResponse: string,
    message: string,
    conversationHistory: Array<{ role: string, content: string }>,
  ) => {
    reader.read().then(({ done, value }: { done: boolean, value: any }) => {
      if (done) {
        const newHistory = [
          ...conversationHistory,
          { role: 'user', content: message },
          { role: 'assistant', content: fullResponse },
        ];
        this.setState({ conversationHistory: newHistory });
        this.finishMessage(fullResponse);
        return;
      }

      const text = decoder.decode(value);
      const newContent = parseSSELines(text);
      const updatedResponse = fullResponse + newContent;

      if (newContent) {
        this.setState(prevState => {
          const newMessages = [...prevState.messages];
          newMessages[newMessages.length - 1] = {
            role: 'assistant',
            content: updatedResponse,
          };
          return { messages: newMessages };
        });
        this.scrollToBottom();
      }

      this.processStreamChunk(reader, decoder, updatedResponse, message, conversationHistory);
    });
  };

  finishMessage = async (fullResponse: string) => {
    const { conversationHistory } = this.state;
    const lastUserMsg = conversationHistory.filter(m => m.role === 'user').pop();
    const userInput = (lastUserMsg && lastUserMsg.content) || '';

    const partNumber = this.detectPartNumber(userInput);
    let foundProduct = null;
    let searchParams;

    if (partNumber && (userInput.includes('대체') || userInput.includes('대신') || userInput.includes('alternative'))) {
      foundProduct = await this.searchByPartNumber(partNumber);

      if (foundProduct) {
        const categoryKeyword = foundProduct.title || foundProduct.category || partNumber;
        searchParams = { keywords: categoryKeyword, maxPrice: null, minPrice: null };
      } else {
        searchParams = { keywords: partNumber, maxPrice: null, minPrice: null };
      }
    } else {
      searchParams = await this.extractSearchParams();
    }

    const { hits, hasMore } = await this.searchProducts(searchParams);

    const products = foundProduct
      ? [foundProduct, ...hits.filter(h => h.id !== foundProduct.id)]
      : hits;

    if (products.length > 0) {
      this.setState(prevState => {
        const newMessages = [...prevState.messages];
        newMessages[newMessages.length - 1] = {
          ...newMessages[newMessages.length - 1],
          products,
          searchParams,
          hasMoreProducts: hasMore,
          originalProduct: foundProduct,
        };
        return { messages: newMessages };
      });
      this.scrollToBottom();
    }

    this.setState({ isProcessing: false });
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  buildMessageContent = async (text: string, files: Array<AttachedFile>): Promise<any> => {
    if (files.length === 0) {
      return text;
    }

    const content: Array<any> = [];

    if (text) {
      content.push({ type: 'text', text });
    }

    const fileContents = await Promise.all(
      files.map(async attachedFile => {
        if (attachedFile.type === 'image') {
          const base64 = await this.fileToBase64(attachedFile.file);
          const mimeType = attachedFile.file.type || 'image/jpeg';
          return {
            type: 'image_url',
            image_url: {
              url: `data:${mimeType};base64,${base64}`,
            },
          };
        }
        return {
          type: 'text',
          text: `[첨부파일: ${attachedFile.file.name}] - 이 파일의 내용을 참고하여 필요한 연구제품을 추천해주세요.`,
        };
      }),
    );

    return [...content, ...fileContents];
  };

  sendMessage = async () => {
    const { inputValue, isProcessing, conversationHistory, messages, attachedFiles } = this.state;
    const message = inputValue.trim();
    if ((!message && attachedFiles.length === 0) || isProcessing) return;

    const filesToSend = [...attachedFiles];
    const displayMessage = message || '첨부파일을 분석해주세요';

    this.setState({
      isProcessing: true,
      inputValue: '',
      attachedFiles: [],
      messages: [...messages, { role: 'user', content: displayMessage }],
    });

    if (this.inputRef.current) {
      this.inputRef.current.style.height = 'auto';
    }

    setTimeout(this.scrollToBottom, 0);

    try {
      const messageContent = await this.buildMessageContent(message || '이 이미지/파일을 분석하여 관련된 연구제품을 추천해주세요.', filesToSend);

      const response = await fetch(`${MEILISEARCH_URL}/chats/research-recommender/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${MEILISEARCH_KEY}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `당신은 캐시바이의 연구제품 전문 AI 어시스턴트입니다.

역할:
- 사용자의 연구실험 요청을 분석합니다
- 어떤 종류의 제품이 필요한지 설명합니다
- 제품 선택 시 고려해야 할 팁을 제공합니다
- 파트넘버(제품번호)로 대체품 요청 시, 해당 제품이 어떤 종류인지 파악하고 대체 가능한 제품 카테고리를 설명합니다

파트넘버 대체품 요청 시:
- 파트넘버에서 제품 종류를 추론합니다 (예: HSC = High Speed Centrifuge)
- 해당 제품의 주요 스펙과 용도를 설명합니다
- 대체품 선택 시 확인해야 할 호환성 포인트를 안내합니다

중요 규칙:
- 구체적인 제품명이나 브랜드를 직접 나열하지 마세요
- 실제 제품은 아래에 자동으로 표시됩니다

응답 형식 (짧고 간결하게):
1. 요청 이해 (1-2문장)
2. 필요한 제품 카테고리 설명
3. 선택 팁 또는 주의사항 (1-2문장)

3-5문장 이내로 짧게 응답하세요.`,
            },
            ...conversationHistory,
            { role: 'user', content: messageContent },
          ],
          stream: true,
        }),
      });

      const reader = response.body && response.body.getReader();
      const decoder = new TextDecoder();

      this.setState(prevState => ({
        messages: [...prevState.messages, { role: 'assistant', content: '' }],
      }));

      if (reader) {
        this.processStreamChunk(reader, decoder, '', displayMessage, conversationHistory);
      } else {
        this.finishMessage('');
      }
    } catch (error) {
      console.error('Error:', error);
      this.setState(prevState => ({
        messages: [
          ...prevState.messages,
          { role: 'assistant', content: '죄송합니다. 오류가 발생했습니다. 다시 시도해주세요.' },
        ],
        isProcessing: false,
      }));
    }
  };

  renderProducts = (products: Array<Object>, hasMore: boolean, messageIndex: number, originalProduct?: Object) => {
    const isOriginal = (p: Object) => originalProduct && p.id === originalProduct.id;

    return (
      <div className="ai-products-section">
        <div className="ai-products-header">
          <h4>{originalProduct ? '검색된 제품 및 대체품' : '추천 제품'}</h4>
          <span className="ai-products-count">{products.length}개</span>
        </div>
        <div className="ai-products-grid">
          {products.map((p, idx) => {
            const productUrl = `/@${p.username || 'cacheby'}/products/${p.url_slug}`;
            return (
              <div className={`ai-product-card ${isOriginal(p) ? 'ai-product-original' : ''}`} key={p.id || idx}>
                {isOriginal(p) && <span className="ai-product-badge">검색된 제품</span>}
                <div className="ai-product-image">
                  {p.thumbnail ? (
                    <img
                      src={p.thumbnail}
                      alt={p.title}
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <h5>{p.title}</h5>
                <div className="ai-product-brand">{p.brand || '캐시바이'}</div>
                <div className="ai-product-price-row">
                  {Number(p.min_price) > 0 ? (
                    <span className="ai-product-price">{Number(p.min_price).toLocaleString()}원</span>
                  ) : (
                    <span className="ai-product-price-inquiry">가격 문의</span>
                  )}
                </div>
                <div className="ai-product-actions">
                  <a href={productUrl} className="ai-product-link">
                    상세보기
                  </a>
                  <button className="ai-product-alt-btn" onClick={() => this.findAlternatives(p)}>
                    대체품
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {hasMore && (
          <button className="ai-load-more-btn" onClick={() => this.loadMoreProducts(messageIndex)}>
            더 보기
          </button>
        )}
      </div>
    );
  };

  renderMarkdown = (content: string) => {
    let html = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br />');

    html = html.replace(/^- (.*?)(<br \/>|$)/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>');

    return { __html: html };
  };

  renderAttachedFiles = () => {
    const { attachedFiles } = this.state;
    if (attachedFiles.length === 0) return null;

    return (
      <div className="ai-attached-files">
        {attachedFiles.map((file, idx) => (
          <div key={idx} className="ai-attached-file">
            {file.type === 'image' ? (
              <img src={file.preview} alt={file.file.name} />
            ) : (
              <div className="ai-file-icon">
                {file.type === 'pdf' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <rect x="8" y="12" width="8" height="6" />
                  </svg>
                )}
              </div>
            )}
            <span className="ai-file-name">{file.file.name}</span>
            <button className="ai-file-remove" onClick={() => this.removeFile(idx)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    );
  };

  render() {
    const { messages, inputValue, isProcessing, attachedFiles } = this.state;
    const hasMessages = messages.length > 0;

    const suggestions = [
      '세포배양 실험 장비 추천',
      'PCR 시약 목록',
      '파트넘버 대체품 찾기',
      '실험실 기본 세팅',
    ];

    return (
      <PageTemplate>
        <header>
          <MainHeadContainer />
          <MainMobileHeadContainer />
        </header>
        <div className={`ai-page ${hasMessages ? '' : 'ai-initial-state'}`}>
          <div className="ai-container">
            <div className="ai-chat-container">
              {hasMessages ? (
                <React.Fragment>
                  <div className="ai-chat-messages" ref={this.chatMessagesRef}>
                    <div className="ai-messages-inner">
                      {messages.map((msg, idx) => (
                        <div key={idx} className={`ai-message ai-message-${msg.role}`}>
                          <div
                            className="ai-message-content"
                            dangerouslySetInnerHTML={msg.role === 'assistant' ? this.renderMarkdown(msg.content) : undefined}
                          >
                            {msg.role === 'user' ? msg.content : null}
                          </div>
                          {msg.products && msg.products.length > 0 && this.renderProducts(msg.products, msg.hasMoreProducts || false, idx, msg.originalProduct)}
                        </div>
                      ))}
                      {isProcessing && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
                        <div className="ai-message ai-message-assistant">
                          <div className="ai-typing">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ai-input-area">
                    <div className="ai-input-wrapper">
                      {this.renderAttachedFiles()}
                      <div className="ai-input-box">
                        <input
                          ref={this.fileInputRef}
                          type="file"
                          accept="image/*,.pdf,.xlsx,.xls"
                          multiple
                          onChange={this.handleFileSelect}
                          style={{ display: 'none' }}
                        />
                        <button
                          className="ai-attach-btn"
                          onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}
                          disabled={isProcessing}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                          </svg>
                        </button>
                        <textarea
                          ref={this.inputRef}
                          className="ai-input"
                          placeholder="무엇이든 물어보세요"
                          value={inputValue}
                          onChange={this.handleInputChange}
                          onKeyDown={this.handleKeyDown}
                          rows={1}
                        />
                        <button
                          className="ai-send-btn"
                          onClick={this.sendMessage}
                          disabled={isProcessing || (!inputValue.trim() && attachedFiles.length === 0)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ) : (
                <div className="ai-welcome">
                  <h1 className="ai-welcome-title">무엇을 도와드릴까요?</h1>
                  <div className="ai-input-area">
                    <div className="ai-input-wrapper">
                      {this.renderAttachedFiles()}
                      <div className="ai-input-box">
                        <input
                          ref={this.fileInputRef}
                          type="file"
                          accept="image/*,.pdf,.xlsx,.xls"
                          multiple
                          onChange={this.handleFileSelect}
                          style={{ display: 'none' }}
                        />
                        <button
                          className="ai-attach-btn"
                          onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}
                          disabled={isProcessing}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                          </svg>
                        </button>
                        <textarea
                          ref={this.inputRef}
                          className="ai-input"
                          placeholder="무엇이든 물어보세요"
                          value={inputValue}
                          onChange={this.handleInputChange}
                          onKeyDown={this.handleKeyDown}
                          rows={1}
                        />
                        <button
                          className="ai-send-btn"
                          onClick={this.sendMessage}
                          disabled={isProcessing || (!inputValue.trim() && attachedFiles.length === 0)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="ai-welcome-suggestions">
                    {suggestions.map((text, idx) => (
                      <button key={idx} className="ai-suggestion-btn" onClick={() => this.sendExample(text)}>
                        {text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageTemplate>
    );
  }
}

export default AI;



// WEBPACK FOOTER //
// ./src/pages/AI.js